import { computed, inject, onMounted } from 'vue';
import { fetchPublicSiteConfig } from '@/services/siteConfig';
import { STOREFRONT_PREVIEW_KEY } from '@/composables/storefrontPreviewContext';
import brand from '@/config/brand';
import { siteContactPublicRef } from '@/composables/siteContactState';
import { applyStorefrontThemeFromConfig } from '@/composables/useStorefrontTheme';
import { useI18n } from '@/composables/useI18n';

export { siteContactPublicRef } from '@/composables/siteContactState';

/** Traduce una lista base (ES) mapeándola contra translations.en por id. */
function mapListWithEn(base, enList) {
  if (!Array.isArray(enList) || !enList.length) return base;
  return base.map((item) => {
    const en = enList.find((x) => x && String(x.id) === String(item.id)) || {};
    return { ...item, label: (en.label || item.label || '') };
  });
}

/** Traduce navbar_links (incluye children anidados) contra translations.en por id. */
function mapNavbarWithEn(base, enList) {
  if (!Array.isArray(enList) || !enList.length) return base;
  const byId = {};
  enList.forEach((n) => { if (n && n.id != null) byId[String(n.id)] = n; });
  return base.map((item) => {
    const en = byId[String(item.id)] || {};
    const next = { ...item, label: (en.label || item.label || '') };
    const enChildren = Array.isArray(en.children) ? en.children : [];
    if (Array.isArray(item.children) && item.children.length) {
      const childById = {};
      enChildren.forEach((c) => { if (c && c.id != null) childById[String(c.id)] = c; });
      next.children = item.children.map((child) => ({
        ...child,
        label: ((childById[String(child.id)] || {}).label) || child.label || '',
      }));
    }
    return next;
  });
}

let loadInflight = null;

function normalizeDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

function finalizeSiteContactLoad(data) {
  siteContactPublicRef.value = data;
  applyStorefrontThemeFromConfig(data);
  return data;
}

export function loadSiteContactPublic() {
  if (siteContactPublicRef.value !== undefined) {
    applyStorefrontThemeFromConfig(siteContactPublicRef.value);
    return Promise.resolve(siteContactPublicRef.value);
  }
  if (loadInflight) return loadInflight;
  loadInflight = fetchPublicSiteConfig()
    .then((data) => finalizeSiteContactLoad(data))
    .catch(() => finalizeSiteContactLoad(null))
    .finally(() => {
      loadInflight = null;
    });
  return loadInflight;
}

export function refreshSiteContactPublic() {
  // NO pasar por undefined: mantiene el valor previo visible mientras se
  // refetcha (evita flash de fallbacks de marca al cambiar el idioma).
  loadInflight = null
  loadInflight = fetchPublicSiteConfig()
    .then((data) => finalizeSiteContactLoad(data))
    .catch(() => {
      // Si el fetch falla, conservar el valor previo si lo había.
      if (siteContactPublicRef.value === undefined) {
        siteContactPublicRef.value = null
      }
    })
    .finally(() => {
      loadInflight = null
    })
  return loadInflight
}


export function useSiteContact() {
  const preview = inject(STOREFRONT_PREVIEW_KEY, null);
  const pickConfig = () => preview ? preview.config.value : siteContactPublicRef.value;
  onMounted(() => {
    if (!preview) loadSiteContactPublic();
  });

  const { locale } = useI18n();

  const contactEmail = computed(() => {
    const c = pickConfig();
    if (c && typeof c === 'object') {
      return c.contact_email || c.email || brand.contactEmail;
    }
    return brand.contactEmail;
  });

  const contactPhoneDisplay = computed(() => {
    const c = pickConfig();
    const raw =
      c && typeof c === 'object'
        ? c.contact_phone || c.phone || c.contact_whatsapp
        : '';
    const digits = normalizeDigits(raw);
    const fallbackDigits = normalizeDigits(brand.contactPhone || brand.contactWhatsapp);
    const d = digits || fallbackDigits;
    if (!d) return '';
    if (d.length >= 10) {
      return `${d.slice(0, 4)} ${d.slice(4)}`.trim();
    }
    return d;
  });

  const contactMailto = computed(() => {
    const e = contactEmail.value;
    return e ? `mailto:${e}` : '#';
  });

  const whatsappDigits = computed(() => {
    const c = pickConfig();
    const raw =
      c && typeof c === 'object'
        ? c.contact_whatsapp || c.contact_phone || c.phone
        : '';
    const d = normalizeDigits(raw);
    return d || normalizeDigits(brand.contactWhatsapp || brand.contactPhone);
  });

  const whatsappHref = computed(() => {
    const num = whatsappDigits.value;
    if (!num) return '#';
    const c = pickConfig();
    const msg = (c && typeof c === 'object' && c.whatsapp_message) || brand.whatsappMessage;
    return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
  });

  const instagramUrl = computed(() => {
    const c = pickConfig();
    const u = c && typeof c === 'object' ? c.social_instagram : '';
    if (u && String(u).trim()) return String(u).trim();
    return brand.socialInstagram || '';
  });

  const storeName = computed(() => {
    const c = pickConfig();
    if (c && typeof c === 'object' && c.store_name) {
      return String(c.store_name).trim();
    }
    return brand.storeName;
  });

  const storeSlogan = computed(() => {
    const c = pickConfig();
    if (c && typeof c === 'object' && c.store_slogan) {
      return String(c.store_slogan).trim();
    }
    return brand.storeSlogan;
  });

  const aboutText = computed(() => {
    const c = pickConfig();
    if (c && typeof c === 'object' && c.about_text) {
      return String(c.about_text).trim();
    }
    return '';
  });

  const faqs = computed(() => {
    const c = pickConfig();
    const base = (c && typeof c === 'object' && Array.isArray(c.faqs))
      ? c.faqs
        .filter((item) => item && item.is_active !== false && item.question && item.answer)
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      : [];
    if (locale.value !== 'en') return base;
    const enList = c?.translations?.en?.faqs || [];
    return base.map((item) => {
      const en = enList.find((x) => x && String(x.id) === String(item.id)) || {};
      return {
        ...item,
        question: en.question || item.question,
        answer: en.answer || item.answer,
      };
    });
  });

  const navbarLinks = computed(() => {
    const c = pickConfig();
    const base = (c && typeof c === 'object' && Array.isArray(c.navbar_links))
      ? c.navbar_links
        .filter((item) => item && item.is_active !== false && item.label && item.href)
        .sort((a, b) => (a.sort_order ?? a.sortOrder ?? 0) - (b.sort_order ?? b.sortOrder ?? 0))
      : [];
    if (locale.value !== 'en') return base;
    return mapNavbarWithEn(base, c?.translations?.en?.navbar_links);
  });

  const footerLinks = computed(() => {
    const c = pickConfig();
    const base = (c && typeof c === 'object' && Array.isArray(c.footer_links))
      ? c.footer_links
        .filter((item) => item && item.is_active !== false && item.label && item.href)
        .sort((a, b) => (a.sort_order ?? a.sortOrder ?? 0) - (b.sort_order ?? b.sortOrder ?? 0))
      : [];
    if (locale.value !== 'en') return base;
    return mapListWithEn(base, c?.translations?.en?.footer_links);
  });

  const socialTiktokUrl = computed(() => {
    const c = pickConfig();
    const u = c && typeof c === 'object' ? c.social_tiktok : '';
    if (u && String(u).trim()) return String(u).trim();
    return brand.socialTiktok || '';
  });

  const socialYoutubeUrl = computed(() => {
    const c = pickConfig();
    const u = c && typeof c === 'object' ? c.social_youtube : '';
    if (u && String(u).trim()) return String(u).trim();
    return brand.socialYoutube || '';
  });

  const socialFacebookUrl = computed(() => {
    const c = pickConfig();
    const u = c && typeof c === 'object' ? c.social_facebook : '';
    if (u && String(u).trim()) return String(u).trim();
    return brand.socialFacebook || '';
  });

  const telHref = computed(() => {
    const c = pickConfig();
    const raw =
      c && typeof c === 'object'
        ? c.contact_phone || c.phone || c.contact_whatsapp
        : '';
    const digits = normalizeDigits(raw);
    const d = digits || normalizeDigits(brand.contactPhone || brand.contactWhatsapp);
    return d ? `tel:${d}` : '#';
  });

  return {
    contactEmail,
    contactPhoneDisplay,
    contactMailto,
    whatsappHref,
    instagramUrl,
    storeName,
    storeSlogan,
    aboutText,
    faqs,
    navbarLinks,
    footerLinks,
    socialTiktokUrl,
    socialYoutubeUrl,
    socialFacebookUrl,
    telHref,
  };
}
